/**
 * DEV-023B (final MVP) · Administration → System Settings hub.
 *
 * Groups the existing system-level administration surfaces so they
 * live together instead of scattered across the Administration
 * overview (Notifications used to masquerade as "System settings"
 * and Data Management + Run identity audit sat as standalone
 * overview items). This hub reuses the EXISTING implementations
 * and permission checks — no duplicate controls, no new business
 * logic.
 *
 *   • Data Management    — reuses the existing `DataManagementTab`
 *                          component (Wipe All Demo Data control).
 *                          Requires `manage_system_settings` OR
 *                          `manage_users` (matches the existing
 *                          Administration tab gate).
 *   • Run Identity Audit — deep-link to the existing
 *                          `/operations/administration/identity-audit`
 *                          route (permission-gated to `manage_users`).
 *
 * NO backend / API / schema / permissions changes. Presentation
 * and navigation grouping only.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Database, ShieldCheck, ArrowUpRight, Lock } from "lucide-react";
import OperationsShell from "@/components/operations/OperationsShell";
import BackButton from "@/components/BackButton";
import PortalHero from "@/components/ui/PortalHero";
import { portalIdentity } from "@/lib/portalIdentity";
import { useAuth } from "@/context/AuthContext";
import DataManagementTab from "./DataManagementTab";

export default function OperationsSystemSettings() {
    const { hasPermission, user } = useAuth();
    const canDataMgmt = hasPermission("manage_system_settings") || hasPermission("manage_users");
    const canIdentityAudit = hasPermission("manage_users");
    const [tab, setTab] = useState(canDataMgmt ? "overview" : "overview");
    const identity = portalIdentity(user?.role);

    const visibleCount = (canDataMgmt ? 1 : 0) + (canIdentityAudit ? 1 : 0);

    return (
        <OperationsShell>
            <div
                className="mx-auto max-w-5xl px-4 md:px-8 pt-6 md:pt-10 pb-16"
                data-testid="ops-system-settings-page"
            >
                <BackButton
                    to="/operations/administration"
                    fallbackHref="/operations/administration"
                    label="Back to Administration"
                    testid="ops-system-settings-back"
                    className="mb-4"
                />

                <PortalHero
                    testid="ops-system-settings-hero"
                    eyebrow={`${identity.eyebrow} · SYSTEM SETTINGS`}
                    title="System Settings"
                    sentence={
                        visibleCount === 0 ? (
                            <>You don't currently have access to any system-level administration surfaces.</>
                        ) : (
                            <>
                                <strong className="text-white font-semibold">{visibleCount}</strong> system {visibleCount === 1 ? "surface" : "surfaces"} available.
                                Reuses the existing Data Management controls and Identity Audit workflow — no duplicate destinations.
                            </>
                        )
                    }
                    ring={{ value: visibleCount, label: visibleCount === 1 ? "surface" : "surfaces" }}
                    tag="Permission-gated"
                />

                {/* Two tabs so Data Management still runs in-page (as
                    it did on the previous Administration tab) while
                    Run Identity Audit gets its own overview card
                    linking to the existing route. Reuses the SAME
                    DataManagementTab component. */}
                <div className="mt-6 border-b border-[#E5E7EB] flex items-center gap-1">
                    <TabBtn active={tab === "overview"} onClick={() => setTab("overview")} testid="ops-system-settings-tab-overview">
                        Overview
                    </TabBtn>
                    {canDataMgmt && (
                        <TabBtn active={tab === "data-management"} onClick={() => setTab("data-management")} testid="ops-system-settings-tab-data-management">
                            Data Management
                        </TabBtn>
                    )}
                </div>

                <div className="mt-6">
                    {tab === "overview" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ops-system-settings-cards">
                            {visibleCount === 0 ? (
                                <div className="md:col-span-2 rounded-xl border border-[#E5E7EB] bg-white p-8 text-center" data-testid="ops-system-settings-no-perms">
                                    <Lock size={24} strokeWidth={1.5} className="mx-auto text-[#4B5565] mb-2" />
                                    <div className="text-sm font-medium text-[#131B2F]">
                                        No system-level administration permissions assigned.
                                    </div>
                                    <div className="text-xs text-[#4B5565] mt-1">
                                        Ask a Security Administrator to grant you Manage System Settings or Manage Users.
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {canDataMgmt && (
                                        <SettingsCard
                                            testid="ops-system-settings-card-data-management"
                                            onClick={() => setTab("data-management")}
                                            icon={Database}
                                            label="Data Management"
                                            desc="Wipe demo data and manage seed content. Opens the existing Data Management controls in this workspace."
                                        />
                                    )}
                                    {canIdentityAudit && (
                                        <SettingsCard
                                            testid="ops-system-settings-card-identity-audit"
                                            to="/operations/administration/identity-audit"
                                            icon={ShieldCheck}
                                            label="Run Identity Audit"
                                            desc="Read-only reconciliation dry-run for engineer and customer duplicates. No changes are made."
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    )}
                    {tab === "data-management" && (
                        <div data-testid="ops-system-settings-data-management-content">
                            <DataManagementTab />
                        </div>
                    )}
                </div>
            </div>
        </OperationsShell>
    );
}

function TabBtn({ active, onClick, children, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium -mb-px border-b-2 transition-colors ${
                active ? "text-[#131B2F] border-[#131B2F]" : "text-[#4B5565] border-transparent hover:text-[#131B2F]"
            }`}
        >
            {children}
        </button>
    );
}

function SettingsCard({ to, onClick, icon: Icon, label, desc, testid }) {
    const inner = (
        <>
            <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-lg bg-[#EEF2FF] text-[#1E3A8A] flex items-center justify-center shrink-0">
                    <Icon size={18} strokeWidth={1.75} />
                </span>
                <div className="text-sm font-semibold text-[#131B2F] flex-1 min-w-0">{label}</div>
                <ArrowUpRight size={14} strokeWidth={1.75} className="text-[#4B5565]" />
            </div>
            <p className="mt-3 text-xs text-[#4B5565] leading-relaxed">{desc}</p>
        </>
    );
    const cls = "rounded-xl border border-[#E5E7EB] bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:border-[#CBD5E1] hover:shadow-md hover:-translate-y-[1px] transition-all block text-left";
    if (to) {
        return <Link to={to} data-testid={testid} className={cls}>{inner}</Link>;
    }
    return (
        <button type="button" onClick={onClick} data-testid={testid} className={cls + " w-full"}>
            {inner}
        </button>
    );
}
